import { HttpClient, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LoginResponse } from '@shared/models/login-response';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { AuthenticationProvider } from '@shared/shared/enums/authentication-provider.enum';
import { BrowserType } from '@shared/shared/enums/browser-type.enum';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { IUser, LogOutResponse } from '@shared/shared/interfaces/user.interfaces';
import { BrowserTypeService } from '@shared/shared/services/browser-type.service';
import { Angulartics2Segment } from 'angulartics2';
import { USER_LOCAL_STORAGE_KEY } from 'app.config';
import { EMPTY, Observable, merge, of, throwError } from 'rxjs';
import { catchError, distinct, filter, finalize, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as MemberProfileStore from '../../member-profile';
import { UserState } from '../../user/reducers';
import * as UserActions from '../actions/user.actions';

enum LoginType {
  Default = 'default-login',
  Oauth = 'default-oauth',
  AutoLogin = 'auto-login',
}
class CustomQueryEncoderHelper implements HttpParameterCodec {
  public encodeKey(k: string): string {
    return encodeURIComponent(k);
  }

  public encodeValue(v: string): string {
    return encodeURIComponent(v);
  }

  public decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  public decodeValue(v: string): string {
    return decodeURIComponent(v);
  }
}

@Injectable()
export class UserEffects {
  private options = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') };
  private isUserLoggedOut: boolean;

  constructor(
    private actions$: Actions,
    private angulartics2Segment: Angulartics2Segment,
    private ngrxStore: Store<{ user: UserState }>,
    private http: HttpClient,
    private router: Router,
    private siteSettingsService: SiteSettingsService,
    private customVarService: CustomisableVariablesService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private browserTypeService: BrowserTypeService
  ) {}

  public autoLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.USER_AUTO_LOGIN),
        distinct(() => Date.now),
        switchMap((action: UserActions.UserAutoLogin) => {
          return this.http.options(ApiEndpoint.USER_AUTO_LOGIN).pipe(
            switchMap(() => this.http.post(ApiEndpoint.USER_AUTO_LOGIN, { token: action.payload }, this.options)),
            switchMap((loginResponse: LoginResponse) =>
              this.performLogin(LoginType.AutoLogin, loginResponse, action.returnURL)
            ),
            catchError(() => {
              this.router.navigateByUrl('/');
              return EMPTY;
            })
          );
        })
      ),
    { dispatch: false }
  );

  public userLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.USER_LOGIN),
      switchMap((action: UserActions.UserLogin) => {
        const params = new HttpParams({ encoder: new CustomQueryEncoderHelper() });
        const body = params.set('email', action.payload.email).set('password', action.payload.password);

        return this.http.options(ApiEndpoint.USER_LOGIN).pipe(
          switchMap(() => this.http.post(ApiEndpoint.USER_LOGIN, body, this.options)),
          switchMap((loginResponse: LoginResponse) => this.performLogin(LoginType.Default, loginResponse)),
          catchError((error) => {
            if (
              error?.error[0]?.message === 'Member is not eligible' &&
              error?.error[0]?.code === 'authentication_failed'
            ) {
              localStorage.setItem('isLoginElgibilityFailed', 'true');
              this.router.navigate(['/product/user-ineligible']);
            } else {
              return of(new UserActions.UserLoginFail(error));
            }
          })
        );
      })
    )
  );

  public userLoginOauth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.USER_LOGIN_OAUTH),
      switchMap((action: UserActions.UserLoginOauth) => {
        const params = new HttpParams({ encoder: new CustomQueryEncoderHelper() });
        const body = params.set('member_id', action.payload.data.memberId).set('user_hash', action.payload.data.hash);
        this.angulartics2Segment.setUserProperties({
          userId: action.payload.data.memberId,
          userIdType: 'Member ID',
        });

        return this.http.options(ApiEndpoint.USER_OAUTH_LOGIN_VIA_HASH).pipe(
          switchMap(() => this.http.post(ApiEndpoint.USER_OAUTH_LOGIN_VIA_HASH, body, this.options)),
          map((loginResponse: LoginResponse) => {
            this.stashLocal(loginResponse);
            // HACK: returnUrl magically changes to undefined during redirects, so we load it from session storage
            const storedReturnUrl = sessionStorage.getItem('returnUrl');
            if (storedReturnUrl) {
              sessionStorage.removeItem('returnUrl');
            }

            let redirectUrl = this.activatedRoute.snapshot.queryParams['returnUrl'];
            const hostname = window.location.hostname;

            if (!redirectUrl) {
              const queryParams = Object.keys(this.activatedRoute.snapshot.queryParams);
              redirectUrl = queryParams ? queryParams[0] : '';
            }

            if (storedReturnUrl) {
              window.location.href = `https://${hostname}${storedReturnUrl}`;
            } else if (
              redirectUrl &&
              decodeURIComponent(redirectUrl) !== '/home' &&
              redirectUrl !== 'undefined' &&
              redirectUrl !== 'memberExists'
            ) {
              //https://tp-staging-visa-lac.tenproduct.com/member-benefits/details/638?utm_medium=Email
              // = email part is cut off, really hard to debug effectivity, going with this 'fix' for now
              window.location.href = `https://${hostname}/${decodeURIComponent(decodeURIComponent(redirectUrl))}=Email`;
            } else {
              window.location.href = `https://${hostname}/landing`;
            }

            return new UserActions.UserLoginOauthSuccess(loginResponse);
          }),
          catchError(() => of(new UserActions.UserLoginOauthFail()))
        );
      })
    )
  );

  public userLoginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.USER_LOGIN_SUCCESS),
        tap(() => {
          let redirectUrl =
            this.activatedRoute.snapshot.queryParams['redirectUrl'] ||
            this.activatedRoute.snapshot.queryParams['returnUrl'];

          const urlParams = new URLSearchParams(window.location.search);
          const siteId = `${urlParams.get('site_id')}`;
          const token = `${urlParams.get('auth_token')}`;

          if (!redirectUrl && siteId === 'null' && token === 'null') {
            const queryParams = Object.keys(this.activatedRoute.snapshot.queryParams);
            redirectUrl = queryParams ? queryParams[0] : '';
          }

          if (document.getElementById('ot-sdk-btn')) {
            document.getElementById('ot-sdk-btn').style.display = 'none';
          }

          if (redirectUrl && decodeURIComponent(redirectUrl) !== '/home' && redirectUrl !== 'undefined') {
            // can't redirect to home
            if (!redirectUrl.startsWith('/')) {
              window.location.href = '/landing';
              return;
            } else {
              window.location.href = `${window.location.origin}${decodeURIComponent(redirectUrl)}`;
              return;
            }
          } else {
            window.location.href = '/landing';
          }
        })
      ),
    { dispatch: false }
  );

  public userViewLocal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.USER_VIEW_LOCAL),
      switchMap(() => {
        return merge(of(localStorage.getItem(USER_LOCAL_STORAGE_KEY))).pipe(
          filter((value) => !!value),
          map((data) => {
            setTimeout(() => {
              this.ngrxStore.dispatch(new MemberProfileStore.ViewProfile(JSON.parse(data).member_profile_id));
            });
            return new UserActions.UserViewLocalSuccess(data);
          })
        );
      })
    )
  );

  public userRefreshAccessToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.USER_REFRESH_ACCESS_TOKEN),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state]) => {
        const body = { token: state.user['user'].accessToken };
        return this.http.post<any>(ApiEndpoint.USER_TOKEN_REFRESH, body).pipe(
          map((result) => {
            const localData: IUser = {
              id: state.user['user'].id,
              isAuthenticated: true,
              member_profile_id: state.user['user'].member_profile_id,
              preferences: [],
            };

            localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(localData));
            return new UserActions.UserRefreshAccessTokenSuccess(result);
          }),
          catchError(() => {
            localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
            this.dialog.closeAll();
            this.router.navigateByUrl('/user/login');
            return of(new UserActions.UserRefreshAccessTokenFail());
          })
        );
      })
    )
  );

  public userLogoutFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.USER_LOGOUT_FAIL),
        tap(() => {
          this.router.navigate(['/user/login']);
        })
      ),
    { dispatch: false }
  );

  public userLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.USER_LOGOUT),
      switchMap(() => {
        if (this.isUserLoggedOut) {
          return EMPTY;
        }
        this.isUserLoggedOut = true;
        return this.http.post(ApiEndpoint.USER_LOGOUT, undefined).pipe(
          map((res: LogOutResponse) => {
            this.siteSettingsService.clearLocal();
            const provider = this.siteSettingsService.settings.authentication_provider.provider;
            if (res?.external_logout_link) {
              localStorage.setItem('logout link', res.external_logout_link);
              window.location.href = res.external_logout_link;
            }
            const isSsoProvider =
              provider === AuthenticationProvider.visa_lac ||
              provider === AuthenticationProvider.creditsaison ||
              provider === AuthenticationProvider.creditsaisondiamond;

            if (!isSsoProvider && !location.href.includes('auto-login')) {
              this.router.navigate(['/user/login']);
            }

            const creditsaisonProviders =
              provider === AuthenticationProvider.creditsaison ||
              provider === AuthenticationProvider.creditsaisondiamond;

            if (creditsaisonProviders) {
              localStorage.clear();
              this.router.navigate(['/logout']);
            }

            this.siteSettingsService.setOneTrustManageCookieBtnVisibility();

            localStorage.removeItem('userActivityTimestamp');
            return new UserActions.UserLogoutSuccess();
          }),
          catchError(() => of(new UserActions.UserLogoutFail())),
          finalize(() => this.siteSettingsService.clearLocal())
        );
      })
    )
  );

  private performLogin(loginType: LoginType, loginResponse: LoginResponse, returnURL = ''): Observable<never> {
    this.stashLocal(loginResponse);

    this.angulartics2Segment.setUserProperties({
      userId: loginResponse.member_id,
      userIdType: 'Member ID',
    });

    this.customVarService.invalidateVariables();
    this.siteSettingsService.invalidateSettings();

    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe(
        (siteSettings: ISiteSettings) => this.performLoginSuccess(loginType, loginResponse, siteSettings, returnURL),
        () => this.performLoginFail(loginType, loginResponse)
      );

    return EMPTY;
  }

  private performLoginSuccess(
    loginType: LoginType,
    loginResponse: LoginResponse,
    siteSettings: ISiteSettings,
    returnURL = ''
  ) {
    const loginResponseURL = new URL(loginResponse.home_url);
    const siteSettingsURL = new URL(siteSettings.domain);

    if (loginResponseURL.origin !== siteSettingsURL.origin) {
      this.performLoginFail(loginType, loginResponse, siteSettings);
    } else {
      if (loginResponse.site_id !== siteSettings.id) {
        this.siteSettingsService.syncSiteId(loginResponse.site_id);
      }

      switch (loginType) {
        case LoginType.Default:
          if (loginResponse.redirect_site) {
            document.location.reload();
          } else {
            this.ngrxStore.dispatch(new UserActions.UserLoginSuccess(loginResponse));
          }
          break;
        case LoginType.Oauth:
          this.ngrxStore.dispatch(new UserActions.UserLoginOauthSuccess(loginResponse));
          break;
        case LoginType.AutoLogin:
          this.ngrxStore.dispatch(new UserActions.UserLoginSuccess(loginResponse));
          window.location.href = returnURL !== '' ? returnURL : '/landing';
          break;
        default:
          throwError(`Invalid login type for: ${loginType}`);
          break;
      }

      this.ngrxStore.dispatch(new MemberProfileStore.ViewProfile(loginResponse.member_profile_id));
    }
  }

  private performLoginFail(type: LoginType, loginResponse: LoginResponse, siteSettings: ISiteSettings = null) {
    this.siteSettingsService.clearLocal();

    this.angulartics2Segment.eventTrack('LoginFailInconsistentSiteId', {
      userSiteId: loginResponse.site_id,
      siteSettingsId: siteSettings.id || null,
      siteSettingsClient: siteSettings?.fe_configuration?.path || null,
    });

    switch (type) {
      case LoginType.Default:
        this.ngrxStore.dispatch(new UserActions.UserLoginFail({ status: 500 }));
        break;
      case LoginType.Oauth:
        this.ngrxStore.dispatch(new UserActions.UserLoginOauthFail());
        break;
      default:
        throwError(`Invalid login type for: ${type}`);
    }
  }

  private stashLocal(loginResponse: LoginResponse) {
    this.siteSettingsService.syncSiteId(loginResponse.site_id);

    const user: IUser = {
      id: loginResponse.user_id,
      isAuthenticated: true,
      member_profile_id: loginResponse.member_profile_id,
      preferences: [],
    };

    localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(user));
    // Safari 13.1+ does not sync session storage between tabs
    if (this.browserTypeService.getBrowserName() === BrowserType.Safari) {
      localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(user));
    }
  }
}
